/* eslint-disable no-console */
import { useApolloClient } from '@apollo/client';
import {
  Details,
  type FormActionData,
  GenericField,
  ReadOnlyField,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { Field } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type GenerateServiceAccountSecretMutation,
  useEnvironmentQuery,
  useGenerateServiceAccountSecretMutation,
  useServiceAccountQuery,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import classes from './ServiceAccountNewClientSecret.module.scss';

const clientSecretDefault = '*********************************';

type GenerateServiceAccountSecretPayload =
  GenerateServiceAccountSecretMutation['generateServiceAccountSecret'];

export const ServiceAccountNewClientSecret: React.FC = () => {
  const { envId, id } = useParams<{
    envId: string;
    id: string;
  }>();

  const apolloClient = useApolloClient();

  // Query service account data
  const { loading, data, error } = useServiceAccountQuery({
    client: apolloClient,
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  // Query Environment Name
  const environmentDetails = useEnvironmentQuery({
    client: apolloClient,
    variables: {
      id: envId,
    },
    fetchPolicy: 'no-cache',
  });
  const envName = environmentDetails.data?.environment?.name;

  const [generateServiceAccountSecret] =
    useGenerateServiceAccountSecretMutation({
      client: apolloClient,
      fetchPolicy: 'no-cache',
    });

  const [serviceAccountCredentials, setServiceAccountCredentials] =
    useState<GenerateServiceAccountSecretPayload>({
      clientSecret: clientSecretDefault,
      serviceAccount: null,
    });

  const isProtected = data?.serviceAccount?.isProtected ?? false;

  useEffect(() => {
    (async () => {
      if (data?.serviceAccount?.isSecretGenerated === false && !isProtected) {
        // A client secret has not been generated for this service account
        // Generate a new client secret
        const response = await generateServiceAccountSecret({
          variables: {
            input: {
              id,
            },
          },
        });
        setServiceAccountCredentials(
          response.data?.generateServiceAccountSecret ?? null,
        );
      }
    })();
  }, [
    data?.serviceAccount?.isSecretGenerated,
    generateServiceAccountSecret,
    id,
    isProtected,
  ]);

  const { actions, newServiceAccountCredentials } = useGenerationActions(
    id,
    serviceAccountCredentials?.clientSecret !== clientSecretDefault,
  );

  useEffect(() => {
    if (newServiceAccountCredentials) {
      setServiceAccountCredentials(newServiceAccountCredentials);
    }
  }, [newServiceAccountCredentials]);

  const Form: React.FC<{
    serviceAccountCredentials: GenerateServiceAccountSecretPayload;
  }> = ({ serviceAccountCredentials }) => {
    const credentialsInBase64 =
      serviceAccountCredentials?.clientSecret === clientSecretDefault
        ? clientSecretDefault
        : btoa(
            `${serviceAccountCredentials?.serviceAccount?.clientId}:${serviceAccountCredentials?.clientSecret}`,
          );

    const onCopyClickedHandler =
      (clickTarget: 'secret' | 'base64') => (): void => {
        try {
          clickTarget === 'secret'
            ? // copy secret to clipboard
              navigator.clipboard.writeText(
                serviceAccountCredentials?.clientSecret ?? '',
              )
            : // copy base64 encoding to clipboard
              navigator.clipboard.writeText(credentialsInBase64);
        } catch (_error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to copy to clipboard');
        }
      };

    const getConfigDownloadUrl = (): string => {
      const serviceAccountConfig = `# Service Account (${serviceAccountCredentials?.serviceAccount?.name}) (Environment: ${envName})
TENANT_ID=${serviceAccountCredentials?.serviceAccount?.tenantId}
ENVIRONMENT_ID=${serviceAccountCredentials?.serviceAccount?.environmentId}
CLIENT_ID=${serviceAccountCredentials?.serviceAccount?.clientId}
CLIENT_SECRET=${serviceAccountCredentials?.clientSecret}
BASE64_ENCODED_CREDENTIALS=${credentialsInBase64}`;

      const blobConfig = new Blob([serviceAccountConfig], {
        type: 'text/plain;charset=utf-8',
      });

      return URL.createObjectURL(blobConfig);
    };

    return (
      <>
        <Field name="clientId" label="Client ID" as={ReadOnlyField} />
        <div
          className={clsx(classes.clientSecret, {
            [classes.hasWarning]:
              serviceAccountCredentials?.clientSecret !== clientSecretDefault,
          })}
        >
          <ReadOnlyField
            value={serviceAccountCredentials?.clientSecret}
            label="Client Secret"
          />
          {serviceAccountCredentials?.clientSecret !== clientSecretDefault && (
            <>
              <TextButton
                className={classes.button}
                text="Copy"
                onButtonClicked={onCopyClickedHandler('secret')}
              />
            </>
          )}
        </div>

        <div
          className={clsx(classes.clientSecret, {
            [classes.hasWarning]:
              serviceAccountCredentials?.clientSecret !== clientSecretDefault,
          })}
        >
          <ReadOnlyField
            value={credentialsInBase64}
            label="Base64 Encoded Credentials"
            tooltipContent={
              'The credentials formated as {CLIENT_ID}:{CLIENT_SECRET} and encoded as a Base64 string. This encoded format can be used when the credentials are expected as HTTP Basic authentication. For example, as the API Key when publishing pilets to the Micro-Frontend Service.'
            }
          />
          {serviceAccountCredentials?.clientSecret !== clientSecretDefault && (
            <>
              <TextButton
                className={classes.button}
                text="Copy"
                onButtonClicked={onCopyClickedHandler('base64')}
              />
            </>
          )}
        </div>

        {serviceAccountCredentials?.clientSecret !== clientSecretDefault && (
          <GenericField
            name="serviceAccountConfiguration"
            label="Service Account Configuration"
          >
            <p>
              <a
                href={getConfigDownloadUrl()}
                target="_blank"
                rel="noreferrer"
                download="mosaic-service-account-config.env"
              >
                Download
              </a>
            </p>
          </GenericField>
        )}
      </>
    );
  };

  const [message, setMessage] = useState<StationMessage>();
  useEffect(() => {
    if (serviceAccountCredentials?.clientSecret !== clientSecretDefault) {
      setMessage({
        title:
          'WARNING: Please copy the client secret displayed below and store it in a secure location or download the credentials stored in a file and store it in a secure location. Once this station is closed, this value cannot be retrieved again and can only be replaced by generating a new client secret.',
        type: 'warning',
      });
    }
  }, [serviceAccountCredentials?.clientSecret]);

  return (
    <Details
      defaultTitle="Client Secret"
      actions={actions}
      initialData={{
        data: data?.serviceAccount,
        loading,
        error: error?.message,
      }}
      saveData={() => Promise.resolve()}
      stationMessage={message}
    >
      <Form serviceAccountCredentials={serviceAccountCredentials} />
    </Details>
  );
};

const useGenerationActions = (
  id: string,
  isNewClientSecret: boolean,
): {
  readonly actions: FormActionData[];
  readonly newServiceAccountCredentials: GenerateServiceAccountSecretPayload;
} => {
  const apolloClient = useApolloClient();
  const [generateServiceAccountSecret] =
    useGenerateServiceAccountSecretMutation({
      client: apolloClient,
      fetchPolicy: 'no-cache',
    });
  // Generate new client secret for the service account
  const [newServiceAccountCredentials, setNewServiceAccountCredentials] =
    useState<GenerateServiceAccountSecretPayload>({
      clientSecret: clientSecretDefault,
      serviceAccount: null,
    });

  const actions: FormActionData[] = [];
  if (!isNewClientSecret) {
    actions.push({
      label: 'Generate New Secret',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Once you generate a new secret, all usages of the previous secret will become invalid and would need to be updated with the new value manually.',
      },
      onActionSelected: async () => {
        const response = await generateServiceAccountSecret({
          variables: {
            input: {
              id,
            },
          },
        });
        setNewServiceAccountCredentials(
          response.data?.generateServiceAccountSecret ?? null,
        );
      },
    });
  }

  return {
    actions,
    newServiceAccountCredentials,
  } as const;
};

export const ServiceAccountNewClientSecretCrumb: BreadcrumbResolver = () =>
  'Client Secret';
